import { Injectable, inject } from '@angular/core';
import { InspectionCategoryCacheService } from './cache/inspection-category-cache-service';
import { ProtocolFurtherActionDto } from '../dtos/protocolFurtherActionDto';
import { InspectionResultType } from './api/model/inspectionResultType';
import { InspectionResultElementType } from './api/model/inspectionResultElementType';
import { PointGroupType } from './api/model/pointGroupType';
import { ClientConfigDBService } from '../core/db/client-config-db-service';
import {
  InspectionWithResultsDto,
  FlatInspectionResultDto,
  FlatInspectionCategoryResultDto,
  InspectionElementDto,
} from './api/model/models';

@Injectable({ providedIn: 'root' })
export class StatusChangeValidationService {
  private inspectionCategoryCacheService = inject(InspectionCategoryCacheService);
  private clientConfigDbService = inject(ClientConfigDBService);

  private hasMissingDefectDescriptionConfiguration: boolean = false;

  public constructor() {
    this.clientConfigDbService.getClientConfig().subscribe(
      (config) => {
        if (config) {
          this.hasMissingDefectDescriptionConfiguration =
            config.textValidationDefectAndSanction === true;
        }
      },
      () => {},
    );
  }

  public getNotDefinedPointsMap(
    inspection: InspectionWithResultsDto,
  ): Map<string, FlatInspectionResultDto[]> {
    const inspectionCategories = this.inspectionCategoryCacheService.getInspectionCategories();
    const notDefinedPointsMap = new Map<string, FlatInspectionResultDto[]>();
    inspection.categoryResults?.forEach((categoryResult) => {
      const notDefinedPointsForCategory = this.getNotDefinedPointsForInspectionElement(
        categoryResult.results ?? undefined,
        this.getCategoryDefinitionByNumber(categoryResult.categoryNumber!, inspectionCategories),
      );
      if (notDefinedPointsForCategory.length && categoryResult.categoryNumber) {
        notDefinedPointsMap.set(categoryResult.categoryNumber, notDefinedPointsForCategory);
      }
    }, notDefinedPointsMap);
    return notDefinedPointsMap;
  }

  public getCategoriesWithMissingDefectSeverity(
    inspection: InspectionWithResultsDto,
  ): FlatInspectionCategoryResultDto[] {
    const inspectionCategories = this.inspectionCategoryCacheService.getInspectionCategories();
    return (
      inspection.categoryResults?.filter((categoryResult) =>
        this.hasCategoryMissingDefectSeverity(categoryResult, inspectionCategories),
      ) || []
    );
  }

  public getCategoriesWithMissingDefectDescription(
    inspection: InspectionWithResultsDto,
  ): FlatInspectionCategoryResultDto[] {
    if (!this.hasMissingDefectDescriptionConfiguration) {
      return [];
    }
    const inspectionCategories = this.inspectionCategoryCacheService.getInspectionCategories();
    return (
      inspection.categoryResults?.filter((categoryResult) =>
        this.hasCategoryMissingDefectDescription(categoryResult, inspectionCategories),
      ) || []
    );
  }

  public getCategoriesWithMissingSanctionDescription(
    inspection: InspectionWithResultsDto,
  ): FlatInspectionCategoryResultDto[] {
    if (!this.hasMissingDefectDescriptionConfiguration) {
      return [];
    }
    const inspectionCategories = this.inspectionCategoryCacheService.getInspectionCategories();
    return (
      inspection.categoryResults?.filter((categoryResult) =>
        this.hasCategoryMissingSanctionDescription(categoryResult, inspectionCategories),
      ) || []
    );
  }

  public isFurtherActionSelectionValid(furtherActions: ProtocolFurtherActionDto) {
    let furtherActionSelectionValid = false;
    if (furtherActions) {
      if (
        furtherActions.noFurtherAction &&
        !furtherActions.inspectionReport &&
        !furtherActions.criminalComplaint &&
        !furtherActions.feedback &&
        !furtherActions.followupInspection &&
        !furtherActions.repairsIndependently &&
        !furtherActions.inspectionReport &&
        !furtherActions.order
      ) {
        furtherActionSelectionValid = true;
      } else if (
        !furtherActions.noFurtherAction &&
        (furtherActions.repairsIndependently ||
          furtherActions.criminalComplaint ||
          furtherActions.feedback ||
          furtherActions.followupInspection ||
          furtherActions.furtherAction ||
          furtherActions.inspectionReport ||
          furtherActions.notification ||
          furtherActions.order)
      ) {
        furtherActionSelectionValid = true;
      }
    }
    return furtherActionSelectionValid;
  }

  private getNotDefinedPointsForInspectionElement(
    categoryResults: FlatInspectionResultDto[] | undefined,
    inspectionElement: InspectionElementDto | undefined,
  ): FlatInspectionResultDto[] {
    const notDefinedResults = new Array<FlatInspectionResultDto>();
    if (inspectionElement) {
      const elementResult = categoryResults?.find(
        (result) => result.inspectionElementKey === inspectionElement.key,
      );
      if (inspectionElement.type === InspectionResultElementType.Point) {
        if (elementResult && !this.hasPointValue(inspectionElement, elementResult)) {
          notDefinedResults.push(elementResult);
        }
      } else {
        if (
          inspectionElement.pointGroupType === PointGroupType.SubheadWithoutDataEntry &&
          inspectionElement.childElements
        ) {
          inspectionElement.childElements.forEach((childElement) => {
            notDefinedResults.push(
              ...this.getNotDefinedPointsForInspectionElement(categoryResults, childElement),
            );
          });
        } else if (
          (inspectionElement.pointGroupType === PointGroupType.Regular ||
            inspectionElement.type === InspectionResultElementType.Category) &&
          inspectionElement.childElements
        ) {
          if (
            !elementResult ||
            (elementResult.inspectionResult !== InspectionResultType.NotApplicable &&
              elementResult.inspectionResult !== InspectionResultType.NotControlled)
          ) {
            inspectionElement.childElements.forEach((childElement) => {
              notDefinedResults.push(
                ...this.getNotDefinedPointsForInspectionElement(categoryResults, childElement),
              );
            });
          }
        }
      }
    }
    return notDefinedResults;
  }

  private hasCategoryMissingDefectSeverity(
    categoryResultDto: FlatInspectionCategoryResultDto,
    inspectionCategories: InspectionElementDto[] | undefined,
  ): boolean {
    const inspectionCategory = this.getCategoryDefinitionByNumber(
      categoryResultDto.categoryNumber!,
      inspectionCategories,
    );
    const categoryResult = categoryResultDto.results?.find(
      (result) => result.inspectionElementKey === inspectionCategory?.key,
    );
    if (categoryResult?.inspectionResult !== InspectionResultType.Defect) {
      return false;
    }
    const displayCategoryResult =
      this.inspectionCategoryCacheService.getDisplayCategoryResult(categoryResultDto);
    if (displayCategoryResult === false) {
      return false;
    }
    return categoryResult && !categoryResult.defectSeverity;
  }

  private hasCategoryMissingDefectDescription(
    categoryResultDto: FlatInspectionCategoryResultDto,
    inspectionCategories: InspectionElementDto[] | undefined,
  ): boolean {
    const inspectionCategory = this.getCategoryDefinitionByNumber(
      categoryResultDto.categoryNumber!,
      inspectionCategories,
    );
    const resultsWithMissingDefectDescription = categoryResultDto.results?.filter(
      (result) =>
        result.inspectionResult === InspectionResultType.Defect &&
        result.inspectionElementKey !== inspectionCategory?.key &&
        !result.defectDescription,
    );
    if (!resultsWithMissingDefectDescription || resultsWithMissingDefectDescription.length === 0) {
      return false;
    }
    const displayCategoryResult =
      this.inspectionCategoryCacheService.getDisplayCategoryResult(categoryResultDto);
    if (displayCategoryResult === false) {
      return false;
    }
    return resultsWithMissingDefectDescription.length > 0;
  }

  private hasCategoryMissingSanctionDescription(
    categoryResultDto: FlatInspectionCategoryResultDto,
    inspectionCategories: InspectionElementDto[] | undefined,
  ): boolean {
    const inspectionCategory = this.getCategoryDefinitionByNumber(
      categoryResultDto.categoryNumber!,
      inspectionCategories,
    );
    const resultsWithMissingSanctionRemark = categoryResultDto.results?.filter(
      (result) =>
        result.inspectionResult === InspectionResultType.Defect &&
        result.inspectionElementKey !== inspectionCategory?.key &&
        !result.remark,
    );
    if (!resultsWithMissingSanctionRemark || resultsWithMissingSanctionRemark.length === 0) {
      return false;
    }
    const displayCategoryResult =
      this.inspectionCategoryCacheService.getDisplayCategoryResult(categoryResultDto);
    if (displayCategoryResult === false) {
      return false;
    }
    return resultsWithMissingSanctionRemark.length > 0;
  }

  private getCategoryDefinitionByNumber(
    categoryNumber: string,
    inspectionCategories: InspectionElementDto[] | undefined,
  ) {
    return inspectionCategories?.find((category) => category.number === categoryNumber);
  }

  private hasPointValue(pointElement: InspectionElementDto, result: FlatInspectionResultDto) {
    let hasPointValue = true;
    if (!result) {
      hasPointValue = false;
    } else if (
      !pointElement.fieldType &&
      result.inspectionResult === InspectionResultType.NotDefined
    ) {
      hasPointValue = false;
    }
    return hasPointValue;
  }
}
